/**
 * Quando sincronizar.
 *
 * A validação nasce no aparelho e só vira verdade para os outros validadores
 * depois de subir. Enquanto ela está aqui, o mesmo ingresso pode entrar em
 * outra portaria — então a regra é simples: subir o quanto antes, sem inundar
 * o servidor numa fila movimentada.
 *
 * Gatilhos:
 *  - logo após validar, agrupando a rajada de leituras numa espera curta;
 *  - a cada minuto, como rede de segurança;
 *  - quando a rede volta, sem esperar o próximo minuto;
 *  - quando o app sai da frente ou volta para ela;
 *  - a cada dez minutos, pedindo o evento inteiro em vez do delta.
 *
 * Esse último existe porque o delta do servidor não traz venda feita depois da
 * abertura do evento: quem comprou um ingresso nominal com a portaria já
 * rodando não aparecia no aparelho, e nem o minuto a minuto o trazia. Pedir do
 * começo é o mesmo caminho da abertura, que traz tudo.
 */

import {AppState, type AppStateStatus} from 'react-native';
import NetInfo, {type NetInfoState} from '@react-native-community/netinfo';

import {sincronizarEvento, type ResultadoSync} from './sincronizar';

/** Espera curta depois de validar: 20 pessoas passando viram um envio só. */
const ESPERA_APOS_VALIDACAO_MS = 5000;

/** Rede de segurança, para o caso de todos os outros gatilhos falharem. */
const INTERVALO_REGULAR_MS = 60000;

/**
 * De quanto em quanto tempo pedir o evento inteiro, e não só o delta.
 *
 * Dez minutos é a troca entre ver a venda nova cedo e não puxar a base toda o
 * tempo inteiro: num evento grande esta chamada traz milhares de nominais, e o
 * aparelho da portaria costuma estar em 4G. Se a venda de última hora for forte
 * a ponto de dez minutos incomodar, é aqui que se mexe.
 */
const INTERVALO_COMPLETO_MS = 600000;

export interface Agendador {
  /** Chame depois de cada validação gravada. */
  pedirEnvio(): void;
  /** Dispara um ciclo agora e espera terminar. */
  agora(): Promise<void>;
  parar(): void;
}

export interface OpcoesAgendador {
  eventoId: number;
  machineId: number;
  aoSincronizar?: (resultado: ResultadoSync) => void;
  aoFalhar?: (erro: Error) => void;
}

export function iniciarAgendador(opcoes: OpcoesAgendador): Agendador {
  const {eventoId, machineId, aoSincronizar, aoFalhar} = opcoes;

  let vivo = true;
  let emAndamento = false;
  /** Pedido que chegou enquanto um ciclo rodava — vira um ciclo novo no fim. */
  let pedidoPendente = false;
  let esperaCurta: ReturnType<typeof setTimeout> | null = null;
  let tinhaRede = true;
  /** O ciclo represado era dos que pedem tudo? Não pode virar um delta. */
  let pendenteDoZero = false;

  async function ciclo(doZero = false): Promise<void> {
    if (!vivo) {
      return;
    }
    if (emAndamento) {
      // Dois ciclos ao mesmo tempo mandariam a mesma validação duas vezes.
      pedidoPendente = true;
      pendenteDoZero = pendenteDoZero || doZero;
      return;
    }

    emAndamento = true;
    try {
      const resultado = await sincronizarEvento(eventoId, machineId, () => {}, doZero);
      if (vivo) {
        aoSincronizar?.(resultado);
      }
    } catch (erro) {
      // Portaria não para por falta de rede: o pendente fica na fila e sobe no
      // próximo gatilho.
      if (vivo) {
        aoFalhar?.(erro as Error);
      }
    } finally {
      emAndamento = false;
      if (vivo && pedidoPendente) {
        pedidoPendente = false;
        const deNovoDoZero = pendenteDoZero;
        pendenteDoZero = false;
        ciclo(deNovoDoZero);
      }
    }
  }

  const relogio = setInterval(() => ciclo(), INTERVALO_REGULAR_MS);
  const relogioCompleto = setInterval(() => ciclo(true), INTERVALO_COMPLETO_MS);

  const inscricaoRede = NetInfo.addEventListener((estado: NetInfoState) => {
    const temRede = Boolean(estado.isConnected);
    // Só no reencontro com a rede — mudança de Wi-Fi para 4G não precisa de ciclo.
    if (temRede && !tinhaRede) {
      ciclo();
    }
    tinhaRede = temRede;
  });

  const inscricaoApp = AppState.addEventListener(
    'change',
    (estado: AppStateStatus) => {
      // Saindo da frente: esvazia a fila antes que o sistema congele o app.
      // Voltando: pega o que os outros aparelhos validaram enquanto isso.
      if (estado === 'background' || estado === 'active') {
        ciclo();
      }
    },
  );

  return {
    pedirEnvio() {
      if (esperaCurta) {
        clearTimeout(esperaCurta);
      }
      esperaCurta = setTimeout(() => {
        esperaCurta = null;
        ciclo();
      }, ESPERA_APOS_VALIDACAO_MS);
    },

    agora: () => ciclo(),

    parar() {
      vivo = false;
      clearInterval(relogio);
      clearInterval(relogioCompleto);
      if (esperaCurta) {
        clearTimeout(esperaCurta);
      }
      inscricaoRede();
      inscricaoApp.remove();
    },
  };
}
